// Internal production page (/intern/produktion).
//
// Deliberately unlisted and visually distinct from the public site: a slim
// "production console" with a horizontal tab bar (Velkommen + one tab per
// stage), a stage image (or placeholder), a long sanitised HTML description
// and a grid of downloadable files. Publicly accessible (no password gate) —
// kept unlisted via robots noindex and by not linking it from navigation.

import { useEffect, useState } from 'react';
import { LogoInline } from '../components/LogoInline.tsx';
import { loadProduction } from '../lib/production.ts';
import { usePageMeta } from '../lib/usePageMeta.ts';
import type { ProductionView } from '../lib/types.ts';

/** Rich-text styling for the sanitised CDN HTML (console-scale, <h2> sections). */
const PROD_RICHTEXT =
  'text-mf-ink text-base leading-relaxed ' +
  '[&_h2]:font-display [&_h2]:text-2xl [&_h2]:text-mf-black [&_h2]:mt-8 [&_h2]:mb-3 ' +
  '[&_h3]:font-display [&_h3]:text-xl [&_h3]:text-mf-black [&_h3]:mt-6 [&_h3]:mb-2 ' +
  '[&_p]:mb-4 ' +
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:space-y-1 ' +
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4 ' +
  '[&_strong]:font-semibold [&_b]:font-semibold ' +
  '[&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-mf-coral ' +
  '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0';

const WELCOME_TAB_ID = 'velkommen';

type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly data: ProductionView };

function ProdBar() {
  return (
    <header className="sticky top-0 z-20 bg-white border-b border-mf-green/15">
      <div className="max-w-5xl mx-auto h-16 px-5 md:px-8 flex items-center justify-between gap-4">
        <a href="/" className="flex items-center gap-3.5" aria-label="Midtfyns Festival – forside">
          <LogoInline className="h-7" />
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-mf-black pl-3.5 border-l border-mf-green/15">
            Produktion
          </span>
        </a>
        <span className="hidden sm:inline font-mono text-[11px] tracking-wider text-mf-ink/55">
          Intern · Scener &amp; downloads
        </span>
      </div>
    </header>
  );
}

function ProdFooter() {
  return (
    <footer className="border-t border-mf-green/15 bg-white">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-4 flex flex-wrap justify-between gap-3 font-mono text-[11px] tracking-wide text-mf-ink/55">
        <span>Midtfyns Festival · Produktion</span>
        <span>Intern side – del ikke linket offentligt</span>
      </div>
    </footer>
  );
}

/** Stage image, or a dashed placeholder when no image is set. */
function ImageSlot({ imageUrl, alt }: { readonly imageUrl: string; readonly alt: string }) {
  if (imageUrl !== '') {
    return (
      <figure className="mb-7">
        <img
          src={imageUrl}
          alt={alt}
          loading="lazy"
          className="block w-full max-h-[380px] object-cover rounded-xl border border-mf-green/15"
        />
      </figure>
    );
  }
  return (
    <figure className="mb-7">
      <div
        role="img"
        aria-label="Intet billede valgt"
        className="flex flex-col items-center justify-center gap-2.5 h-60 rounded-xl border-2 border-dashed border-mf-green/20 bg-mf-grey/40 text-mf-ink/55"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="w-11 h-11 opacity-60"
        >
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <circle cx="9" cy="10" r="1.6" />
          <path d="M3.5 17.5l5-5 4 4 3-3 5 5" />
        </svg>
        <span className="font-mono text-[11px] uppercase tracking-[0.14em]">Intet billede valgt</span>
      </div>
    </figure>
  );
}

export default function ProduktionPage() {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [activeTab, setActiveTab] = useState<string>(WELCOME_TAB_ID);

  // Unlisted page: its own title and robots noindex, through the same hook every page uses.
  usePageMeta({
    title: 'Produktion',
    description: 'Intern produktionsside for Midtfyns Festival.',
    noIndex: true,
  });

  // A stable scrollbar gutter, so switching between short and tall tabs doesn't shift the layout.
  useEffect(() => {
    const previousGutter = document.documentElement.style.scrollbarGutter;
    document.documentElement.style.scrollbarGutter = 'stable';
    return () => { document.documentElement.style.scrollbarGutter = previousGutter; };
  }, []);

  // Load production.json on mount.
  useEffect(() => {
    let cancelled = false;
    loadProduction()
      .then(data => {
        if (cancelled) return;
        setState({ status: 'ready', data });
        // Honour a #stage-<slug> deep link.
        const hash = window.location.hash.replace(/^#/, '');
        if (hash === WELCOME_TAB_ID || data.stages.some(s => `stage-${s.slug}` === hash)) {
          setActiveTab(hash);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Ukendt fejl',
        });
      });
    return () => { cancelled = true; };
  }, []);

  function selectTab(id: string) {
    setActiveTab(id);
    window.history.replaceState(null, '', `#${id}`);
  }

  return (
    <div className="min-h-screen flex flex-col bg-mf-grey/30">
      <ProdBar />

      <main className="flex-1 w-full max-w-5xl mx-auto px-5 md:px-8 pt-6 pb-12">
        {state.status === 'loading' && (
          <div className="bg-white border border-mf-green/15 rounded-2xl p-14 text-center text-mf-ink/60">
            Henter produktionsdata …
          </div>
        )}

        {state.status === 'error' && (
          <div className="bg-white border border-mf-green/15 rounded-2xl p-14 text-center">
            <h1 className="font-display text-2xl text-mf-black mb-2">
              Produktionsinformation er ikke tilgængelig
            </h1>
            <p className="text-mf-ink/60">Prøv igen lidt senere. ({state.message})</p>
          </div>
        )}

        {state.status === 'ready' && (
          <>
            {/* Horizontal tabs: Velkommen + one per stage */}
            <nav
              className="flex gap-1.5 overflow-x-auto bg-white border border-mf-green/15 rounded-2xl p-1.5 mb-5"
              role="tablist"
              aria-label="Produktion"
            >
              {[
                { id: WELCOME_TAB_ID, label: state.data.welcome.title },
                ...state.data.stages.map(s => ({ id: `stage-${s.slug}`, label: s.name })),
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => selectTab(tab.id)}
                  className={`shrink-0 px-5 py-2.5 rounded-xl font-display font-semibold text-base whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'bg-mf-green text-white'
                      : 'text-mf-black hover:bg-mf-coral/10'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>

            {/* Panel */}
            <div className="bg-white border border-mf-green/15 rounded-2xl px-6 py-8 md:px-9 md:py-9 min-h-[55vh]">
              {activeTab === WELCOME_TAB_ID && (
                <section role="tabpanel" aria-label={state.data.welcome.title}>
                  <header className="border-b border-mf-green/15 pb-4 mb-6">
                    <h1 className="font-display font-bold text-3xl md:text-4xl text-mf-black">
                      {state.data.welcome.title}
                    </h1>
                  </header>
                  <ImageSlot imageUrl={state.data.welcome.imageUrl} alt={state.data.welcome.title} />
                  <div
                    className={PROD_RICHTEXT}
                    dangerouslySetInnerHTML={{ __html: state.data.welcome.bodyHtml }}
                  />
                </section>
              )}

              {state.data.stages.map(stage =>
                activeTab === `stage-${stage.slug}` ? (
                  <section key={stage.slug} role="tabpanel" aria-label={stage.name}>
                    <header className="border-b border-mf-green/15 pb-4 mb-6">
                      <h1 className="font-display font-bold text-3xl md:text-4xl text-mf-black">
                        {stage.name}
                      </h1>
                    </header>

                    <ImageSlot imageUrl={stage.imageUrl} alt={stage.name} />

                    {stage.descriptionHtml !== '' && (
                      <div
                        className={PROD_RICHTEXT}
                        dangerouslySetInnerHTML={{ __html: stage.descriptionHtml }}
                      />
                    )}

                    <section className="mt-9 pt-7 border-t border-mf-green/15">
                      <h2 className="flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.18em] text-mf-ink/55 mb-4">
                        Downloads
                        <span className="text-[11px] bg-mf-grey/50 border border-mf-green/15 rounded-full px-2.5 py-px text-mf-ink">
                          {stage.files.length}
                        </span>
                      </h2>

                      {stage.files.length === 0 ? (
                        <p className="italic text-mf-ink/55">Ingen filer til denne scene endnu.</p>
                      ) : (
                        <ul className="grid gap-3 sm:grid-cols-2">
                          {stage.files.map(file => (
                            <li key={file.url}>
                              <a
                                href={file.url}
                                {...(file.isImage
                                  ? { download: true }
                                  : { target: '_blank', rel: 'noopener noreferrer' })}
                                className="group flex items-center gap-3.5 h-full p-3.5 bg-white border border-mf-green/15 rounded-xl hover:border-mf-coral hover:shadow-lg hover:-translate-y-px transition"
                              >
                                <span
                                  aria-hidden="true"
                                  className="shrink-0 w-[52px] h-[52px] rounded-lg overflow-hidden border border-mf-green/15 bg-mf-grey/40 flex items-center justify-center"
                                >
                                  {file.isImage ? (
                                    <img src={file.url} alt="" loading="lazy" className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="w-full h-full flex items-center justify-center bg-mf-green text-white font-mono font-bold text-[11px] tracking-wider">
                                      PDF
                                    </span>
                                  )}
                                </span>
                                <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                                  <span className="font-semibold text-mf-ink leading-tight truncate">
                                    {file.title}
                                  </span>
                                  <span className="font-mono text-[11px] text-mf-ink/55 truncate">
                                    {file.fileName}
                                    {file.sizeLabel !== '' && ` · ${file.sizeLabel}`}
                                  </span>
                                </span>
                                <span className="shrink-0 font-mono text-[11px] uppercase tracking-wider text-mf-coral">
                                  {file.isImage ? 'Hent' : 'Åbn'}
                                </span>
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  </section>
                ) : null,
              )}
            </div>
          </>
        )}
      </main>

      <ProdFooter />
    </div>
  );
}
